import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, StarIcon, ShoppingBagIcon } from 'lucide-react';
import { Product } from '../data/products';
import { useStore } from '../context/StoreContext';
import { ShareButton } from './ShareButton';
interface ProductCardProps {
  product: Product;
}
export function ProductCard({
  product
}: ProductCardProps) {
  const {
    toggleWishlist,
    isInWishlist,
    getApplicablePromo
  } = useStore();
  const isWishlisted = isInWishlist(product.id);
  const applicablePromo = getApplicablePromo(product.id);
  const discountPercent = Math.round((product.mrp - product.sellingPrice) / product.mrp * 100);
  return <div className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col h-full border border-slate-100" data-id="element-237">
      {/* Badges */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-2" data-id="element-238">
        {discountPercent > 0 && <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded" data-id="element-239">
            {discountPercent}% OFF
          </span>}
        {product.isNewArrival && <span className="bg-sky-500 text-white text-xs font-bold px-2 py-1 rounded" data-id="element-240">
            NEW
          </span>}
      </div>
      {/* Action Buttons */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2" data-id="element-241">
        <button onClick={e => {
        e.preventDefault();
        toggleWishlist(product.id);
      }} className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors" aria-label="Toggle wishlist" data-id="element-242">
          <HeartIcon className={`w-5 h-5 transition-colors ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-slate-400 hover:text-red-500'}`} data-id="element-243" />
        </button>
        <ShareButton productId={product.id} productName={product.name} productPrice={product.sellingPrice} promoCode={applicablePromo?.code} data-id="element-244" />
      </div>
      {/* Image */}
      <Link to={`/product/${product.id}`} className="block relative aspect-[4/5] overflow-hidden bg-slate-100" data-id="element-245">
        <img src={product.images[0]} alt={product.name} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-246" />
        {/* Quick Add Overlay on Desktop */}
        <div className="absolute inset-x-0 bottom-0 p-4 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 hidden md:block bg-gradient-to-t from-black/60 to-transparent" data-id="element-247">
          <div className="bg-white text-sky-600 font-medium py-2 rounded text-center text-sm shadow-sm flex items-center justify-center gap-2" data-id="element-248">
            <ShoppingBagIcon className="w-4 h-4" data-id="element-249" />
            View Details
          </div>
        </div>
      </Link>
      {/* Content */}
      <div className="p-4 flex flex-col flex-grow" data-id="element-250">
        <div className="mb-1 text-xs text-slate-500 font-medium uppercase tracking-wider" data-id="element-251">
          {product.category}
        </div>
        <Link to={`/product/${product.id}`} className="block group-hover:text-sky-600 transition-colors" data-id="element-252">
          <h3 className="font-medium text-slate-900 line-clamp-2 mb-2 text-sm md:text-base" data-id="element-253">
            {product.name}
          </h3>
        </Link>

        <div className="mt-auto" data-id="element-254">
          <div className="flex items-center gap-1 mb-2" data-id="element-255">
            <div className="flex items-center bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-medium" data-id="element-256">
              <span data-id="element-257">{product.ratings}</span>
              <StarIcon className="w-3 h-3 ml-0.5 fill-current" data-id="element-258" />
            </div>
            <span className="text-xs text-slate-400" data-id="element-259">
              ({product.reviewCount})
            </span>
          </div>

          <div className="flex items-baseline gap-2" data-id="element-260">
            <span className="text-lg font-bold text-slate-900" data-id="element-261">
              ₹{product.sellingPrice}
            </span>
            {product.mrp > product.sellingPrice && <span className="text-sm text-slate-400 line-through" data-id="element-262">
                ₹{product.mrp}
              </span>}
          </div>
        </div>
      </div>
    </div>;
}